import type React from "react";
import { motion } from "framer-motion";
import { FiBarChart2, FiExternalLink, FiShield } from "react-icons/fi";
import { easeManor } from "../../constants/constants";
import { analyticsDashboardUrl, isAnalyticsConfigured } from "../../lib/analytics";

const LinkCard: React.FC<{
  href: string;
  title: string;
  blurb: string;
  icon: React.ReactNode;
  delay?: number;
}> = ({ href, title, blurb, icon, delay = 0 }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.8, ease: easeManor, delay }}
    whileHover={{ y: -3 }}
    className="card-parchment group relative block px-7 py-8 rounded-sm no-underline"
  >
    <div className="absolute inset-2 border border-[color:var(--gilt-hairline)] pointer-events-none" />
    <div className="flex items-start justify-between gap-4">
      <span className="w-11 h-11 rounded-full grid place-items-center border border-[color:var(--gilt-deep)] text-[color:var(--claret)]">
        {icon}
      </span>
      <FiExternalLink
        size={16}
        className="text-[color:var(--gilt-deep)] opacity-60 group-hover:opacity-100 transition-opacity"
      />
    </div>
    <p className="mt-5 u-label">{title}</p>
    <p className="mt-3 font-display text-2xl text-[color:var(--claret-deep)] leading-tight">
      {blurb}
    </p>
  </motion.a>
);

const AnalyticsPanel: React.FC = () => {
  return (
    <section>
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="u-label">The Observatory</p>
          <h3 className="mt-2 font-display text-3xl md:text-4xl text-[color:var(--claret-deep)]">
            Who has called at the <em>manor</em>.
          </h3>
          <p className="mt-2 text-[color:var(--ink-soft)] max-w-xl">
            Cloudflare Web Analytics records every visit — pageviews, unique
            visitors, top pages, countries, and referrers — privately, without
            cookies, and without a consent banner. Free for commercial use.
          </p>
        </div>
        {!isAnalyticsConfigured && (
          <p className="u-label text-[color:var(--claret)] max-w-xs">
            Set REACT_APP_CLOUDFLARE_ANALYTICS_URL in your env to link directly
            to your site's dashboard.
          </p>
        )}
      </div>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <LinkCard
          href={analyticsDashboardUrl}
          title="Cloudflare Web Analytics"
          blurb="Visitors, pageviews, referrers, Core Web Vitals"
          icon={<FiBarChart2 size={18} />}
        />
        <LinkCard
          href="https://dash.cloudflare.com/?to=/:account/web-analytics"
          title="Privacy & Consent"
          blurb="No cookies set, no personal data stored, GDPR-safe by design"
          icon={<FiShield size={18} />}
          delay={0.08}
        />
      </div>

      <p className="mt-10 text-sm italic text-[color:var(--ink-soft)]">
        Custom events (e.g. reservation clicks, gallery opens) are recorded
        in-code but not uploaded — Cloudflare's free tier is pageview-only. If
        you ever want per-click tracking, swap in CloudWatch RUM or PostHog;
        the <code>trackEvent</code> helper in{" "}
        <code>src/lib/analytics.ts</code> is already wired to every call site.
      </p>
    </section>
  );
};

export default AnalyticsPanel;
